import React from 'react';
import _get from 'lodash/get';
import { TFunction } from 'i18next';
import { User, UserRoleEnum } from 'graphql-common';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import UserSetupCell from '@lib/components/Table/commonComponents/UserSetupCell/UserSetupCell';
import CollapseCell from '@lib/components/Table/commonComponents/CollapseCell/CollapseCell';
import { CellProps, Column } from '@lib/components/Table/types';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import { getRoleOption } from '@lib/utils/roles';
import Tooltip from '@lib/components/Tooltip/Tooltip';

type ColumnsArgs = {
  currentUserId?: string;
  isViewOnly: boolean;
  onResendEmailConfirmationHandler: (id: string) => void;
  onResendRegistrationConfirmationHandler: (id: string) => void;
  onViewSectorHandler: (id: string) => void;
  onViewSiteHandler: (id: string) => void;
  onViewTeamHandler: (id: string) => void;
  showRoleColumn?: boolean;
  showSiteSectorColumn?: boolean;
  t: TFunction<'translation', undefined>;
};

export default function getUsersTableColumns({
  currentUserId,
  isViewOnly,
  onResendEmailConfirmationHandler,
  onResendRegistrationConfirmationHandler,
  onViewSectorHandler,
  onViewSiteHandler,
  onViewTeamHandler,
  showRoleColumn = true,
  showSiteSectorColumn = true,
  t,
}: ColumnsArgs) {
  const columns: Column<User>[] = [
    {
      id: 'personalId',
      Header: 'ID',
      accessor: 'personalId',
      Cell: (props: CellProps<User>) => <TextCell {...props} />,
      minWidth: 80,
      width: 80,
      sortable: true,
    },
    {
      id: 'fullName',
      Header: t('name'),
      Cell: ({ item }: CellProps<User>) => (
        <UserPreviewCell
          avatar={
            item.avatarAttached?.urls?.small ||
            item.avatarAttached?.urls?.original
          }
          name={`${item.firstName} ${item.lastName}`}
          email={item.email}
        />
      ),
      minWidth: 308,
      width: 308,
      sortable: true,
    },
  ];
  if (showRoleColumn) {
    columns.push({
      id: 'role',
      Header: t('role'),
      accessorFn: ({ item }: CellProps<User>) => {
        const role = getRoleOption(item.role)?.labelKey;
        return role ? t(role) : '-';
      },
      Cell: (props: CellProps<User>) => <TextCell {...props} />,
      maxWidth: 150,
      minWidth: 150,
      width: 150,
      sortable: true,
    });
  }
  columns.push({
    id: 'teamName',
    Header: t('team'),
    accessorFn: ({ item }: CellProps<User>) => {
      const { team } = item;
      if (team?.id) {
        return (
          <div>
            <Chip
              type={team.color as ChipTypes}
              label={team.name}
              leftIcon="circle"
              onClick={() => onViewTeamHandler(team.id)}
            />
          </div>
        );
      }
      return '-';
    },
    maxWidth: 150,
    minWidth: 150,
    width: 150,
    sortable: true,
  });
  if (showSiteSectorColumn) {
    columns.push({
      id: 'site-or-sector',
      Header: `${t('sector')} / ${t('site')}`,
      Cell: (props: CellProps<User>) => {
        const role = _get(props, 'item.role');
        const sites = _get(props, 'item.sites');
        const sectors = _get(props, 'item.sectors');
        if (role === UserRoleEnum.FullAdmin) {
          return (
            <div>
              <Chip type={ChipTypes.chipGray} label={t('all')} asBlockElement />
            </div>
          );
        }
        return sites?.collection?.length || sectors?.collection?.length ? (
          <CollapseCell
            {...props}
            minHeight={24}
            value={
              <>
                {sites.collection.map(({ id, name }) => (
                  <div style={{ maxWidth: '100%' }} key={id}>
                    <Chip
                      key={id}
                      type={ChipTypes.chipGray}
                      label={name}
                      onClick={() => onViewSiteHandler(id)}
                    />
                  </div>
                ))}
                {sectors.collection.map(({ id, name }) => (
                  <div style={{ maxWidth: '100%' }} key={id}>
                    <Chip
                      key={id}
                      type={ChipTypes.chipGray}
                      label={name}
                      leftIcon="circle"
                      onClick={() => onViewSectorHandler(id)}
                    />
                  </div>
                ))}
              </>
            }
          />
        ) : (
          <div>
            <Tooltip body={t('user-have-no-access-site-sector')}>
              <Chip type={ChipTypes.chipOrange} leftIcon="warning" />
            </Tooltip>
          </div>
        );
      },
      maxWidth: 200,
      minWidth: 200,
      width: 200,
    });
  }
  columns.push({
    id: 'setup',
    Header: 'Setup',
    Cell: (props: CellProps<User>) => (
      <UserSetupCell
        {...props}
        isViewOnly={isViewOnly}
        currentUserId={currentUserId}
        onResendEmailConfirmationHandler={() =>
          onResendEmailConfirmationHandler(_get(props, 'item.id'))
        }
        onResendRegistrationConfirmationHandler={() =>
          onResendRegistrationConfirmationHandler(_get(props, 'item.id'))
        }
      />
    ),
    hideHeader: true,
    minWidth: 204,
    width: 204,
    prohibitDisabled: true,
  });
  return columns;
}
